import React, { createContext, useContext, useEffect, useState } from 'react';
import { storage } from '../utils/storage';
import {
  AppSettings,
  SETTINGS_DEFAULTS,
  SETTINGS_STORAGE_PREFIX,
} from '../config/settings';

interface SettingsContextValue {
  settings: AppSettings;
  /** False until persisted values have been read off disk on startup. */
  loaded: boolean;
  setSetting: <K extends keyof AppSettings>(
    key: K,
    value: AppSettings[K],
  ) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

/**
 * Loads device-local settings once on mount and exposes a setter that persists
 * each change via the platform storage util. Settings start at SETTINGS_DEFAULTS
 * and are overlaid with stored values as they load (fast — local storage), so a
 * brief flash of defaults is possible but in practice settings finish loading
 * well before the dashboard mounts (operator login + checklist happen first).
 */
export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(SETTINGS_DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const keys = Object.keys(SETTINGS_DEFAULTS) as (keyof AppSettings)[];
      const entries = await Promise.all(
        keys.map(async (key) => {
          const raw = await storage.getItem(SETTINGS_STORAGE_PREFIX + String(key));
          return [key, raw] as const;
        }),
      );
      if (cancelled) return;
      setSettings((prev) => {
        const next = { ...prev };
        for (const [key, raw] of entries) {
          if (raw === null) continue;
          try {
            (next as Record<string, unknown>)[key as string] = JSON.parse(raw);
          } catch {
            // Corrupt value — keep the default.
          }
        }
        return next;
      });
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setSetting = async <K extends keyof AppSettings>(
    key: K,
    value: AppSettings[K],
  ): Promise<void> => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    await storage.setItem(SETTINGS_STORAGE_PREFIX + String(key), JSON.stringify(value));
  };

  return (
    <SettingsContext.Provider value={{ settings, loaded, setSetting }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return ctx;
}
